import { normalizeStatus, PUBLISH_STATUS_LABELS } from '@/lib/publishStatus';
import { resolveValue } from '@/lib/multilingual';
import { dayMonthYear } from '@/lib/serverTime';

/*
 * How a passport's changelog entries read: what kind of entry each is, and
 * its values as text. Shared by the feed (ChangelogFeed) and the editor's CSV
 * export.
 */

/**
 * What kind of entry a value entry is: `'erased'` for one the end of life
 * purge redacted (its values deleted with the property, `erased: true`, who
 * and when kept), else `'changed'` when it had an old value and `'created'`
 * when it did not.
 */
export function changelogAction(event) {
    if (isErasedEvent(event)) {
        return 'erased';
    }

    return event.old_value ? 'changed' : 'created';
}

/**
 * How many loaded entries each history filter shows: all, the value entries
 * by changelogAction() (changed, created, and erased by the end of life
 * purge), and the status entries. All is the sum of the others.
 */
export function changelogCounts(changelog) {
    const list = changelog ?? [];
    const values = (action) => list.filter((event) => !isPublishStatusEvent(event) && changelogAction(event) === action).length;

    return {
        all: list.length,
        changed: values('changed'),
        created: values('created'),
        erased: values('erased'),
        status: list.filter(isPublishStatusEvent).length,
    };
}

/** The entries a history filter (`all`, `changed`, `created`, `erased`, `status`) shows; the CSV export takes the same. */
export function filterChangelog(changelog, filter) {
    return (changelog ?? []).filter((event) => {
        switch (filter) {
            case 'status':
                return isPublishStatusEvent(event);
            case 'changed':
            case 'created':
            case 'erased':
                return !isPublishStatusEvent(event) && changelogAction(event) === filter;
            default:
                return true;
        }
    });
}

/** A value entry the end of life purge redacted: it shows no value, ever. */
export function isErasedEvent(event) {
    return event?.erased === true;
}

/**
 * How a redacted entry reads in the feed (with its property's name before
 * it) and in the CSV: the entry was an update, made by its own actor at its
 * own time, whose value the end of life purge deleted since.
 */
export const ERASED_SENTENCE = 'updated, value deleted at end of life';
export const ERASED_CSV_ACTION = 'Updated, value deleted at end of life';

/**
 * A change's new value as the feed shows it, or '' when the change cleared
 * the field: no value at all, or one that shows as nothing (an empty list,
 * a translated value with every language removed). The feed then says
 * "cleared" instead of ending at the arrow.
 */
export function changedToText(value, property, defaultLanguage, { dayFirst = false } = {}) {
    if (value === null || value === undefined) {
        return '';
    }

    return displayValue(value, property, defaultLanguage, { dayFirst }).trim();
}

// Publish status changelog entries use the reserved property key
// 'publish_status' instead of an ontology property id.
export function isPublishStatusEvent(event) {
    return event.property === 'publish_status';
}

/**
 * The latest release, read off the changelog's status entries (oldest first,
 * as the passport read embeds them), or undefined when none is loaded. It is
 * the entry that made the release: a re-release with no changes returns to
 * the release it was revising, which keeps its number and its date.
 */
export function latestRelease(changelog) {
    const released = (changelog ?? []).filter((event) => isPublishStatusEvent(event) && event.new_value === 'released');
    const latest = released.at(-1);
    const number = Number(latest?.release_number ?? 0);

    return number ? released.find((event) => Number(event.release_number ?? 0) === number) : latest;
}

/**
 * For each publish-status entry of `events` (newest first, as the feed shows
 * them), the release number the passport was at before it: the number on the
 * nearest older status entry that carries one, or null when none is loaded.
 * What tells a re-release with no changes from a new release (see
 * publishStatusLabel).
 */
export function previousReleaseNumbers(events) {
    const previous = new Map();
    let older = null;
    for (const event of [...(events ?? [])].reverse()) {
        if (!isPublishStatusEvent(event)) {
            continue;
        }
        previous.set(event, older);
        if (event.release_number != null) {
            older = Number(event.release_number);
        }
    }

    return previous;
}

/**
 * The badge a release made by an external change carries (ReleaseBadge on the
 * server), by its stored value: a service event, an end of life, or a
 * correction of an earlier external change.
 */
export const RELEASE_BADGE_LABELS = {
    service_event: 'Service event',
    end_of_life: 'End of life',
    correction: 'Correction',
};

/**
 * A publish-status changelog entry, named — for the feed and the CSV export.
 *
 * A status entry carries the passport's release number after the move
 * (`release_number`), so:
 *
 * - `→ released` is "Released as #n", followed by its badge when an
 *   external change made it ("Released as #n · Service event", see
 *   RELEASE_BADGE_LABELS; a badge this code does not know adds nothing);
 *   from revising, at the number the passport was already at
 *   (`previousReleaseNumber`, see previousReleaseNumbers), nothing had
 *   changed and it is "Released again as #n (no changes)";
 * - `released → revising` is "Revising (release #n unlocked)";
 * - `→ archived` is "Archived", and "Archived · End of life" for the marker
 *   an end of life save wrote (badged `end_of_life`), with or without a
 *   release number;
 * - `→ draft` is "Draft", and "Draft (was release #n)" for a release
 *   cancelled back to draft (history from before revising existed);
 * - an absent status reads as draft, as legacy history has it.
 */
export function publishStatusLabel(entry, previousReleaseNumber = null) {
    const status = normalizeStatus(entry?.new_value);
    const number = Number(entry?.release_number ?? 0);
    const label = PUBLISH_STATUS_LABELS[status] ?? status;

    if (status === 'archived' && entry?.badge === 'end_of_life') {
        return `${label} · ${RELEASE_BADGE_LABELS.end_of_life}`;
    }
    if (!number) {
        return label;
    }
    if (status === 'released') {
        if (entry.old_value === 'revising' && Number(previousReleaseNumber ?? 0) === number) {
            return `Released again as #${number} (no changes)`;
        }
        const badge = Object.hasOwn(RELEASE_BADGE_LABELS, entry.badge ?? '') ? RELEASE_BADGE_LABELS[entry.badge] : null;

        return badge ? `Released as #${number} · ${badge}` : `Released as #${number}`;
    }
    if (status === 'revising') {
        return `Revising (release #${number} unlocked)`;
    }

    return status === 'draft' ? `${label} (was release #${number})` : label;
}

/**
 * Who made a changelog entry, as the feed and the CSV export name them:
 * `{ name, team, external, rule }`, or null when there is no one to show.
 *
 * The server names the actor (ChangelogActorNames). Without a name, Synex is
 * "Synex" and a user who no longer exists is "Deleted user"; an actor of a
 * kind this code does not know, unnamed, is not shown at all. A change a
 * Synex admin made signed in as a user is "Synex support (as {name})": the
 * server never sends the admin's name, only their id as the marker
 * (`impersonated_by`). `team` is the supplier or service partner an
 * external actor acted for, as the passport's team knows it, or null.
 * `rule` is the edit rule an external change was made under (`rule_name`),
 * or null for any other change and for a rule since deleted. Consul's
 * history tool (PassportHistory) uses the same words.
 */
export function changelogActor(actor) {
    if (!actor) {
        return null;
    }
    let name = actor.name ?? (actor.type === 'system' ? 'Synex' : actor.type === 'user' ? 'Deleted user' : null);
    if (name === null) {
        return null;
    }
    if (actor.impersonated_by != null) {
        name = `Synex support (as ${name})`;
    }
    const external = actor.origin === 'external';

    return { name, team: external ? actor.team_name ?? null : null, external, rule: actor.rule_name ?? null };
}

/**
 * The changelog as the editor's CSV export writes it, header row first: one
 * row per entry of `events`, in their order, with who made it in the last two
 * columns ("Changed By", with an external actor's team in parentheses, and
 * "Origin"), both blank for an entry from before actors were recorded.
 * `allEvents` is the whole loaded history, oldest first, which a status entry
 * is named against (see previousReleaseNumbers), so a filter can never change
 * what one is called. `propertyFor(id)`: as ChangelogFeed's. An entry the
 * end of life purge redacted reads "Updated, value deleted at end of life"
 * with both values blank, whatever it still holds.
 */
export function changelogCsvRows(events, allEvents, propertyFor) {
    const header = ['Property', 'Action', 'Old Value', 'New Value', 'Updated At', 'Changed By', 'Origin'];
    const previousRelease = previousReleaseNumbers([...(allEvents ?? [])].reverse());
    const rows = (events ?? []).map((event) => {
        const action = event.old_value ? 'Changed' : 'Created';
        const actor = changelogActor(event.actor);
        const by = actor ? [actor.team ? `${actor.name} (${actor.team})` : actor.name, actor.external ? 'External' : 'Internal'] : ['', ''];
        if (isPublishStatusEvent(event)) {
            return [
                'Publish Status',
                action,
                event.old_value ? PUBLISH_STATUS_LABELS[normalizeStatus(event.old_value)] ?? event.old_value : '',
                publishStatusLabel(event, previousRelease.get(event)),
                event.updated_at,
                ...by,
            ];
        }
        const property = propertyFor(event.property);
        if (isErasedEvent(event)) {
            return [property?.name || 'Unknown Property', ERASED_CSV_ACTION, '', '', event.updated_at, ...by];
        }

        return [
            property?.name || 'Unknown Property',
            action,
            event.old_value ? changelogValue(event.old_value, property?.type) : '',
            changelogValue(event.new_value, property?.type),
            event.updated_at,
            ...by,
        ];
    });

    return [header, ...rows].map((row) => row.map((cell) => String(cell ?? '')));
}

/** A changelog value as text; `dayFirst` writes a date day first ("11 Sep 2026") instead of in the browser's locale. */
export function changelogValue(eventValue, propertyType = null, { dayFirst = false } = {}) {
    switch (propertyType) {
        case 'short_text':
        case 'long_text':
        case 'number':
        case 'measurement':
            return typeof eventValue === 'object' && eventValue !== null ? JSON.stringify(eventValue) : eventValue;
        case 'boolean':
            return eventValue ? 'true' : 'false';
        case 'date':
            return (dayFirst && dayMonthYear(eventValue)) || new Date(eventValue).toLocaleDateString();
        case 'file':
        case 'image':
            return eventValue?.filename || 'Unknown file';
        default:
            return typeof eventValue === 'string' ? eventValue : JSON.stringify(eventValue);
    }
}

/** A changelog value as the feed shows it: one language, with its unit (`dayFirst`: see changelogValue). */
export function displayValue(value, property, defaultLanguage, { dayFirst = false } = {}) {
    let shown = value;
    if (shown && typeof shown === 'object' && !Array.isArray(shown) && !['file', 'image', 'range'].includes(property?.type)) {
        shown = resolveValue(shown, null, defaultLanguage).value;
    }
    if (Array.isArray(shown)) {
        return shown.join(', ');
    }
    const text = changelogValue(shown, property?.type, { dayFirst });
    const unit = property?.type === 'measurement' ? property?.value?.measurement : null;

    return unit ? `${text} ${unit}` : String(text ?? '');
}
